// src/edu-world/systems/micro-interaction-animations.ts
// Proximity-based animations for all 24 micro-interactions in EduWorld.
// When the player walks near a MI, it triggers a looping tween animation.
// When the player walks away, the animation stops gracefully.

import Phaser from 'phaser';

/** Animation configuration for each MI type */
interface MIAnimationConfig {
  /** Function that creates and starts the animation, returns cleanup function */
  play: (scene: Phaser.Scene, sprite: Phaser.GameObjects.Image) => () => void;
}

/** Tracks active animations per MI */
interface ActiveAnimation {
  cleanup: () => void;
  isPlaying: boolean;
  /** If true, the proximity system won't stop this animation (e.g. fly-away) */
  locked: boolean;
}

/**
 * MicroInteractionAnimator
 * Manages proximity-based animations for all 24 MI sprites.
 * Each frame, checks distance from player to each MI.
 * If within trigger radius → starts animation.
 * If outside → stops animation gracefully.
 */
export class MicroInteractionAnimator {
  private scene: Phaser.Scene;
  private sprites: Map<string, Phaser.GameObjects.Image> = new Map();
  /** Stores the original spawn position of each MI for distance checks */
  private originalPositions: Map<string, { x: number; y: number }> = new Map();
  private activeAnimations: Map<string, ActiveAnimation> = new Map();
  private triggerRadius: number;

  constructor(scene: Phaser.Scene, triggerRadius: number = 80) {
    this.scene = scene;
    this.triggerRadius = triggerRadius;
  }

  /** Register a MI sprite for proximity animation */
  registerSprite(id: string, sprite: Phaser.GameObjects.Image): void {
    this.sprites.set(id, sprite);
    this.originalPositions.set(id, { x: sprite.x, y: sprite.y });
  }

  /** Called every frame with player position */
  update(playerX: number, playerY: number): void {
    for (const [id] of this.sprites) {
      // Use original position for distance check (sprite may have moved due to animation)
      const orig = this.originalPositions.get(id)!;
      const dx = playerX - orig.x;
      const dy = playerY - orig.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const active = this.activeAnimations.get(id);

      if (dist <= this.triggerRadius) {
        // Player is close — start animation if not already playing
        if (!active || !active.isPlaying) {
          this.startAnimation(id);
        }
      } else {
        // Player is far — stop animation if playing (unless locked)
        if (active && active.isPlaying && !active.locked) {
          this.stopAnimation(id);
        }
      }
    }
  }

  private startAnimation(id: string): void {
    const sprite = this.sprites.get(id);
    const config = ANIMATION_CONFIGS[id];
    if (!config || !sprite) return;

    const cleanup = config.play(this.scene, sprite);
    const isLocked = LOCKED_ANIMATIONS.has(id);
    this.activeAnimations.set(id, { cleanup, isPlaying: true, locked: isLocked });
  }

  private stopAnimation(id: string): void {
    const active = this.activeAnimations.get(id);
    if (active) {
      active.cleanup();
      active.isPlaying = false;
      active.locked = false;
    }
  }

  /** Cleanup all animations (on scene shutdown) */
  destroy(): void {
    for (const [id] of this.activeAnimations) {
      this.stopAnimation(id);
    }
    this.sprites.clear();
    this.originalPositions.clear();
    this.activeAnimations.clear();
  }
}

/** MIs that should NOT be stopped by proximity (they fly away and return on their own) */
const LOCKED_ANIMATIONS = new Set(['mi-helice', 'mi-chamas', 'mi-tractor']);

// ─── Helper: reset sprite to original state ───

function resetSprite(sprite: Phaser.GameObjects.Image, origX: number, origY: number, origScale: number, origAngle: number): void {
  sprite.x = origX;
  sprite.y = origY;
  sprite.setScale(origScale);
  sprite.setAngle(origAngle);
  sprite.setAlpha(0.85);
}

// ─── Animation Configs for each MI ───

const ANIMATION_CONFIGS: Record<string, MIAnimationConfig> = {

  // ── Quinta ──

  'mi-tractor': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      const tweens: Phaser.Tweens.Tween[] = [];
      // Small vibration before moving
      const t1 = scene.tweens.add({
        targets: sprite,
        y: origY - 1,
        duration: 60,
        yoyo: true,
        repeat: 5,
        ease: 'Sine.easeInOut',
      });
      tweens.push(t1);
      // Drive forward (to the left, matching sprite orientation) and off screen
      const t2 = scene.tweens.add({
        targets: sprite,
        x: origX - 500,
        alpha: 0,
        duration: 2500,
        delay: 400,
        ease: 'Quad.easeIn',
        onComplete: () => {
          sprite.x = origX;
          sprite.y = origY;
          sprite.setAlpha(0);
          const t3 = scene.tweens.add({
            targets: sprite,
            alpha: 0.85,
            duration: 800,
            delay: 1500,
            ease: 'Sine.easeOut',
          });
          tweens.push(t3);
        },
      });
      tweens.push(t2);
      return () => {
        tweens.forEach(t => t.stop());
        resetSprite(sprite, origX, origY, origScale, origAngle);
      };
    },
  },

  'mi-espantalho': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Birds fly up (particles) + sprite sways
      const birds: Phaser.GameObjects.Text[] = [];
      const t1 = scene.tweens.add({
        targets: sprite,
        angle: { from: -3, to: 3 },
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      // Spawn 3 birds that fly up
      for (let i = 0; i < 3; i++) {
        const bird = scene.add.text(
          sprite.x + Phaser.Math.Between(-20, 20),
          sprite.y - 10,
          '🐦', { fontSize: '10px' }
        ).setDepth(15).setAlpha(0);
        birds.push(bird);
        scene.tweens.add({
          targets: bird,
          alpha: 1, y: bird.y - 40 - i * 15,
          x: bird.x + Phaser.Math.Between(-30, 30),
          duration: 800, delay: i * 300,
          ease: 'Quad.easeOut',
          onComplete: () => {
            scene.tweens.add({
              targets: bird, alpha: 0, y: bird.y - 20,
              duration: 400, ease: 'Sine.easeIn',
              onComplete: () => bird.destroy(),
            });
          },
        });
      }
      return () => {
        t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle);
        birds.forEach(b => { if (b.active) b.destroy(); });
      };
    },
  },

  // ── Praia ──

  'mi-castelo-areia': {
    play: (scene, sprite) => {
      const origScale = sprite.scaleX;
      const origX = sprite.x;
      const origY = sprite.y;
      const origAngle = sprite.angle;
      // Scale up bounce (grows)
      const t1 = scene.tweens.add({
        targets: sprite,
        scaleX: origScale * 1.15,
        scaleY: origScale * 1.15,
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Back.easeOut',
      });
      return () => { t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  'mi-peixe-saltar': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Y bounce + splash particles
      const t1 = scene.tweens.add({
        targets: sprite,
        y: origY - 12,
        duration: 400,
        yoyo: true,
        repeat: -1,
        repeatDelay: 800,
        ease: 'Quad.easeOut',
      });
      // Splash droplets on each jump
      const particles: Phaser.GameObjects.Arc[] = [];
      const timer = scene.time.addEvent({
        delay: 1600,
        loop: true,
        callback: () => {
          for (let i = 0; i < 3; i++) {
            const drop = scene.add.circle(
              sprite.x + Phaser.Math.Between(-8, 8), sprite.y + 5,
              2, 0x81d4fa
            ).setDepth(14).setAlpha(0.8);
            particles.push(drop);
            scene.tweens.add({
              targets: drop,
              y: drop.y - Phaser.Math.Between(10, 25),
              alpha: 0, duration: 400, delay: i * 60,
              ease: 'Quad.easeOut',
              onComplete: () => drop.destroy(),
            });
          }
        },
      });
      return () => {
        t1.stop(); timer.destroy();
        resetSprite(sprite, origX, origY, origScale, origAngle);
        particles.forEach(p => { if (p.active) p.destroy(); });
      };
    },
  },

  // ── Ilha Pirata ──

  'mi-canhao': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Shake + confetti particles shooting up
      const t1 = scene.tweens.add({
        targets: sprite,
        x: origX + 3,
        duration: 100,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      const confettiColors = [0xff4444, 0xffdd44, 0x44ff44, 0x4488ff, 0xff44ff];
      const particles: Phaser.GameObjects.Arc[] = [];
      const timer = scene.time.addEvent({
        delay: 1200,
        loop: true,
        callback: () => {
          for (let i = 0; i < 5; i++) {
            const c = scene.add.circle(
              sprite.x + Phaser.Math.Between(-5, 5), sprite.y - 5,
              3, confettiColors[i % confettiColors.length]
            ).setDepth(15).setAlpha(1);
            particles.push(c);
            scene.tweens.add({
              targets: c,
              y: c.y - Phaser.Math.Between(30, 60),
              x: c.x + Phaser.Math.Between(-25, 25),
              alpha: 0, duration: 700, delay: i * 50,
              ease: 'Quad.easeOut',
              onComplete: () => c.destroy(),
            });
          }
        },
      });
      return () => {
        t1.stop(); timer.destroy();
        resetSprite(sprite, origX, origY, origScale, origAngle);
        particles.forEach(p => { if (p.active) p.destroy(); });
      };
    },
  },

  'mi-coco': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Y drop + bounce ease (falls and bounces)
      const t1 = scene.tweens.add({
        targets: sprite,
        y: origY + 8,
        duration: 300,
        ease: 'Bounce.easeOut',
        yoyo: true,
        repeat: -1,
        repeatDelay: 1500,
      });
      return () => { t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  // ── Diversões ──

  'mi-carrossel': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Spin on vertical axis (scaleX flips rapidly to simulate Y-axis rotation to the right)
      const t1 = scene.tweens.add({
        targets: sprite,
        scaleX: -origScale,
        duration: 130,
        yoyo: true,
        repeat: -1,
        ease: 'Linear',
      });
      return () => { t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  'mi-balao': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Float up + alpha fade (balloon releases and rises)
      const t1 = scene.tweens.add({
        targets: sprite,
        y: origY - 20,
        alpha: 0.4,
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      // Gentle sway
      const t2 = scene.tweens.add({
        targets: sprite,
        x: origX + 4,
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      return () => { t1.stop(); t2.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  // ── Praça ──

  'mi-fonte': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // ScaleY pulse (water jet effect)
      const t1 = scene.tweens.add({
        targets: sprite,
        scaleY: origScale * 1.08,
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      return () => { t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  'mi-relogio': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Rotation tick + scale pulse (clock chimes)
      const t1 = scene.tweens.add({
        targets: sprite,
        angle: { from: -2, to: 2 },
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Stepped',
      });
      const t2 = scene.tweens.add({
        targets: sprite,
        scaleX: origScale * 1.03,
        scaleY: origScale * 1.03,
        duration: 500,
        yoyo: true,
        repeat: -1,
        repeatDelay: 1500,
        ease: 'Sine.easeOut',
      });
      return () => { t1.stop(); t2.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  // ── Escola ──

  'mi-baloico': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Rotation oscillation (swinging back and forth)
      const t1 = scene.tweens.add({
        targets: sprite,
        angle: { from: -10, to: 10 },
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      return () => { t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  'mi-pegadas': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Alpha fade in sequence (footprints appearing)
      const t1 = scene.tweens.add({
        targets: sprite,
        alpha: { from: 0.3, to: 1 },
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      return () => { t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  // ── Aeroporto ──

  'mi-helice': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      const tweens: Phaser.Tweens.Tween[] = [];
      // Small tilt before takeoff
      const t1 = scene.tweens.add({
        targets: sprite,
        angle: 15,
        duration: 400,
        ease: 'Sine.easeOut',
      });
      tweens.push(t1);
      // Fly off screen — to the left and up (flies forward)
      const t2 = scene.tweens.add({
        targets: sprite,
        x: origX - 600,
        y: origY - 300,
        alpha: 0,
        duration: 2000,
        delay: 500,
        ease: 'Quad.easeIn',
        onComplete: () => {
          // Return to original position invisibly, then fade back in
          sprite.x = origX;
          sprite.y = origY;
          sprite.setAngle(origAngle);
          sprite.setAlpha(0);
          const t3 = scene.tweens.add({
            targets: sprite,
            alpha: 0.85,
            duration: 800,
            delay: 1500,
            ease: 'Sine.easeOut',
          });
          tweens.push(t3);
        },
      });
      tweens.push(t2);
      return () => {
        tweens.forEach(t => t.stop());
        resetSprite(sprite, origX, origY, origScale, origAngle);
      };
    },
  },

  'mi-manga-vento': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Rotation oscillation (wind blowing)
      const t1 = scene.tweens.add({
        targets: sprite,
        angle: { from: -15, to: 15 },
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      return () => { t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  // ── Zoo ──

  'mi-animal-zoo': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Y bounce (monkey jumping)
      const t1 = scene.tweens.add({
        targets: sprite,
        y: origY - 8,
        duration: 350,
        yoyo: true,
        repeat: -1,
        repeatDelay: 600,
        ease: 'Quad.easeOut',
      });
      return () => { t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  'mi-pato': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Circular x/y path (duck swimming in circle)
      let angle = 0;
      const radius = 8;
      const timer = scene.time.addEvent({
        delay: 50,
        loop: true,
        callback: () => {
          angle += 0.05;
          sprite.x = origX + Math.cos(angle) * radius;
          sprite.y = origY + Math.sin(angle) * radius;
        },
      });
      return () => { timer.destroy(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  // ── Bombeiros ──

  'mi-sirene': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Alpha pulse + tint change (flashing siren light)
      const t1 = scene.tweens.add({
        targets: sprite,
        alpha: { from: 0.5, to: 1 },
        duration: 300,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      // Tint alternation via timer
      let tintState = false;
      const timer = scene.time.addEvent({
        delay: 400,
        loop: true,
        callback: () => {
          tintState = !tintState;
          sprite.setTint(tintState ? 0xff4444 : 0xffaa00);
        },
      });
      return () => {
        t1.stop(); timer.destroy();
        sprite.clearTint();
        resetSprite(sprite, origX, origY, origScale, origAngle);
      };
    },
  },

  'mi-mangueira': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Shake + blue water particles
      const t1 = scene.tweens.add({
        targets: sprite,
        x: origX + 2,
        duration: 80,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      const particles: Phaser.GameObjects.Arc[] = [];
      const timer = scene.time.addEvent({
        delay: 300,
        loop: true,
        callback: () => {
          for (let i = 0; i < 3; i++) {
            const drop = scene.add.circle(
              sprite.x + 5, sprite.y - 5,
              2, 0x42a5f5
            ).setDepth(14).setAlpha(0.9);
            particles.push(drop);
            scene.tweens.add({
              targets: drop,
              x: drop.x + Phaser.Math.Between(10, 30),
              y: drop.y + Phaser.Math.Between(-10, 10),
              alpha: 0, duration: 400, delay: i * 50,
              ease: 'Quad.easeOut',
              onComplete: () => drop.destroy(),
            });
          }
        },
      });
      return () => {
        t1.stop(); timer.destroy();
        resetSprite(sprite, origX, origY, origScale, origAngle);
        particles.forEach(p => { if (p.active) p.destroy(); });
      };
    },
  },

  // ── Castelo ──

  'mi-ponte': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Y oscillation (drawbridge rising and lowering)
      const t1 = scene.tweens.add({
        targets: sprite,
        y: origY - 5,
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      return () => { t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  'mi-poco': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Bucket going up (child object rising)
      const bucket = scene.add.text(sprite.x, sprite.y, '🪣', {
        fontSize: '10px',
      }).setOrigin(0.5).setDepth(14).setAlpha(0);
      const t1 = scene.tweens.add({
        targets: bucket,
        alpha: 1,
        y: sprite.y - 15,
        duration: 1000,
        yoyo: true,
        repeat: -1,
        repeatDelay: 800,
        ease: 'Sine.easeInOut',
      });
      return () => { t1.stop(); bucket.destroy(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  // ── Espaço ──

  'mi-chamas': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      const tweens: Phaser.Tweens.Tween[] = [];
      const particles: Phaser.GameObjects.Arc[] = [];
      // Shake (engine ignition)
      const t1 = scene.tweens.add({
        targets: sprite,
        x: origX + 2,
        duration: 50,
        yoyo: true,
        repeat: 12,
        ease: 'Sine.easeInOut',
      });
      tweens.push(t1);
      // Exhaust particles during launch
      const timer = scene.time.addEvent({
        delay: 80,
        repeat: 20,
        callback: () => {
          const p = scene.add.circle(
            sprite.x + Phaser.Math.Between(-4, 4),
            sprite.y + 10,
            3, Phaser.Math.Between(0, 1) ? 0xff6600 : 0xffcc00
          ).setDepth(14).setAlpha(0.9);
          particles.push(p);
          scene.tweens.add({
            targets: p,
            y: p.y + 25,
            alpha: 0,
            scaleX: 2, scaleY: 2,
            duration: 500,
            ease: 'Quad.easeOut',
            onComplete: () => p.destroy(),
          });
        },
      });
      // Launch — fly off screen upward (big distance)
      const t2 = scene.tweens.add({
        targets: sprite,
        y: origY - 600,
        alpha: 0,
        duration: 1800,
        delay: 700,
        ease: 'Quad.easeIn',
        onComplete: () => {
          // Return invisibly, then fade back in
          sprite.x = origX;
          sprite.y = origY;
          sprite.setAlpha(0);
          const t3 = scene.tweens.add({
            targets: sprite,
            alpha: 0.85,
            duration: 800,
            delay: 2000,
            ease: 'Sine.easeOut',
          });
          tweens.push(t3);
        },
      });
      tweens.push(t2);
      return () => {
        tweens.forEach(t => t.stop());
        timer.destroy();
        particles.forEach(p => { if (p.active) p.destroy(); });
        resetSprite(sprite, origX, origY, origScale, origAngle);
      };
    },
  },

  'mi-antena': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Expanding signal rings
      const rings: Phaser.GameObjects.Arc[] = [];
      const timer = scene.time.addEvent({
        delay: 800,
        loop: true,
        callback: () => {
          const ring = scene.add.circle(sprite.x, sprite.y - 10, 5, 0x64b5f6, 0)
            .setStrokeStyle(1, 0x64b5f6)
            .setDepth(14).setAlpha(0.8);
          rings.push(ring);
          scene.tweens.add({
            targets: ring,
            scaleX: 4, scaleY: 4,
            alpha: 0,
            duration: 1200,
            ease: 'Sine.easeOut',
            onComplete: () => ring.destroy(),
          });
        },
      });
      return () => {
        timer.destroy();
        rings.forEach(r => { if (r.active) r.destroy(); });
        resetSprite(sprite, origX, origY, origScale, origAngle);
      };
    },
  },

  // ── Parque (extras) ──

  'mi-borboleta': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // x/y path + rotation (butterfly flying path)
      let angle = 0;
      const radiusX = 12;
      const radiusY = 6;
      const timer = scene.time.addEvent({
        delay: 50,
        loop: true,
        callback: () => {
          angle += 0.06;
          sprite.x = origX + Math.cos(angle) * radiusX;
          sprite.y = origY + Math.sin(angle * 2) * radiusY;
          sprite.angle = Math.sin(angle) * 10;
        },
      });
      return () => { timer.destroy(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

  'mi-sapo': {
    play: (scene, sprite) => {
      const origX = sprite.x;
      const origY = sprite.y;
      const origScale = sprite.scaleX;
      const origAngle = sprite.angle;
      // Y bounce (frog jumping)
      const t1 = scene.tweens.add({
        targets: sprite,
        y: origY - 10,
        duration: 300,
        yoyo: true,
        repeat: -1,
        repeatDelay: 1200,
        ease: 'Quad.easeOut',
      });
      return () => { t1.stop(); resetSprite(sprite, origX, origY, origScale, origAngle); };
    },
  },

};
